import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, UserIcon, BriefcaseIcon, TruckIcon, HeartIcon, ClockIcon, PhoneIcon, PrinterIcon, CreditCardIcon, CheckCircleIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { Pricing } from '../components/Pricing';
import { HowItWorks } from '../components/HowItWorks';
import { FAQ } from '../components/FAQ';
export function PricingPage() {
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <>
      <PageBanner title="Booking & Pricing" subtitle="Transparent pricing and comprehensive information for all our fingerprinting services" data-id="element-374" />

      {/* Existing Pricing Hero Card */}
      <Pricing data-id="element-375" />

      {/* Main Content Section */}
      <section className="py-16 bg-white" data-id="element-376">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-377">
          {/* Policy Notice Banner */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeInUp} className="mb-16 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-6 shadow-sm" data-id="element-378">
            <div className="flex items-start gap-4" data-id="element-379">
              <AlertCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-380" />
              <div data-id="element-381">
                <h3 className="text-lg font-bold text-amber-900 mb-2" data-id="element-382">
                  Important Policy Update
                </h3>
                <p className="text-amber-800 leading-relaxed mb-4" data-id="element-383">
                  Effective October 1, 2020, all in-state applicant fingerprints
                  submitted to CJIS for the purpose of licensing and/or
                  employment background checks must be done electronically via
                  approved live scan fingerprinting units. The only time
                  in-state ink-and-rolled 'hard-card' applicant fingerprint
                  cards will be accepted will be when there is a bona-fide
                  medical reason a live scan transmission cannot be obtained.
                </p>
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900 bg-amber-100/50 inline-flex px-3 py-1.5 rounded-lg" data-id="element-384">
                  <MailIcon className="h-4 w-4" data-id="element-385" />
                  <span data-id="element-386">
                    Questions? Email:{' '}
                    <a href="mailto:cjis.customerservice@maryland.gov" className="underline hover:text-amber-700 transition-colors" data-id="element-387">
                      cjis.customerservice@maryland.gov
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Detailed Information Cards */}
          <div className="space-y-8 mb-24" data-id="element-388">
            {/* Card A: Individual Background Check */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeInUp} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 hover:shadow-md transition-shadow" data-id="element-389">
              <div className="flex items-center gap-4 mb-6" data-id="element-390">
                <div className="p-3 bg-[#F0F4FF] rounded-xl" data-id="element-391">
                  <UserIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-392" />
                </div>
                <h3 className="text-2xl font-bold text-[#1E1F55]" data-id="element-393">
                  Individual Background Check
                </h3>
              </div>
              <p className="text-gray-500 mb-8 font-medium" data-id="element-394">
                For Visas, Gold Seal Letters, and Individual Review
              </p>

              <div className="grid md:grid-cols-2 gap-8" data-id="element-395">
                {/* In Maryland */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-396">
                  <h4 className="font-bold text-[#1E1F55] mb-4 flex items-center gap-2" data-id="element-397">
                    <MapPinIcon className="h-5 w-5 text-[#4AA6CF]" data-id="element-398" />
                    If you live within Maryland:
                  </h4>
                  <ul className="space-y-3 text-gray-600" data-id="element-399">
                    <li className="flex items-start gap-2" data-id="element-400">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-401" />
                      <span data-id="element-402">
                        You may go to any authorized location that provides
                        fingerprinting services.
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-403">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-404" />
                      <span data-id="element-405">
                        If you are requesting a copy of your Federal Bureau of
                        Investigation (FBI) criminal history record you must
                        send your request directly to the FBI.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Outside Maryland */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-406">
                  <h4 className="font-bold text-[#1E1F55] mb-4 flex items-center gap-2" data-id="element-407">
                    <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-408" />
                    If you live Outside Maryland:
                  </h4>
                  <ul className="space-y-4 text-gray-600 text-sm" data-id="element-409">
                    <li data-id="element-410">
                      <strong data-id="element-411">Write to:</strong>
                      <br data-id="element-412" />
                      CJIS - Central Repository
                      <br data-id="element-413" />
                      P.O. Box 32708
                      <br data-id="element-414" />
                      Pikesville, Maryland 21282-2708
                    </li>
                    <li data-id="element-415">
                      <strong data-id="element-416">Call:</strong>
                      <br data-id="element-417" />
                      <a href="tel:4107644501" className="text-[#4AA6CF] hover:underline" data-id="element-418">
                        410-764-4501
                      </a>{' '}
                      or Toll Free{' '}
                      <a href="tel:18887950011" className="text-[#4AA6CF] hover:underline" data-id="element-419">
                        1-888-795-0011
                      </a>
                    </li>
                    <li data-id="element-420">
                      <strong data-id="element-421">Overnight Mail:</strong>
                      <br data-id="element-422" />
                      CJIS - Central Repository
                      <br data-id="element-423" />
                      6776 Reisterstown Road, Suite 217
                      <br data-id="element-424" />
                      Baltimore, Maryland 21215-2346
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-sm rounded-lg border border-blue-100" data-id="element-425">
                    <strong data-id="element-426">Note:</strong> Include a check made out to "CJIS
                    Central Repository". Response expected within 10-15 days.
                    Fingerprint cards from other states and/or the FBI cannot be
                    accepted.
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card B: Employment or Licensing */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeInUp} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 hover:shadow-md transition-shadow" data-id="element-427">
              <div className="flex items-center gap-4 mb-6" data-id="element-428">
                <div className="p-3 bg-[#F0F4FF] rounded-xl" data-id="element-429">
                  <BriefcaseIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-430" />
                </div>
                <h3 className="text-2xl font-bold text-[#1E1F55]" data-id="element-431">
                  Employment or Licensing
                </h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-432">
                If you are seeking employment or to be licensed within Maryland,
                your employer or licensing agency must request the background
                record check.
              </p>
              <div className="bg-[#F0F4FF] rounded-xl p-6 border border-[#E7ECF8]" data-id="element-433">
                <ul className="space-y-4 text-[#1E1F55] font-medium" data-id="element-434">
                  <li className="flex items-center gap-3" data-id="element-435">
                    <div className="w-2 h-2 rounded-full bg-[#4AA6CF]" data-id="element-436" />
                    You MUST use the fingerprint cards supplied by your employer
                    or licensing agency.
                  </li>
                  <li className="flex items-center gap-3" data-id="element-437">
                    <div className="w-2 h-2 rounded-full bg-[#4AA6CF]" data-id="element-438" />
                    You MUST have your employer's or licensing agency's
                    authorization number available.
                  </li>
                </ul>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-439">
              {/* Card C: CDL */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeInUp} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 hover:shadow-md transition-shadow" data-id="element-440">
                <div className="flex items-center gap-4 mb-6" data-id="element-441">
                  <div className="p-3 bg-[#F0F4FF] rounded-xl" data-id="element-442">
                    <TruckIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-443" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E1F55]" data-id="element-444">
                    CDL Hazmat Endorsement
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-445">
                  The Transportation Security Administration passed regulations
                  requiring commercial drivers that transport hazardous
                  materials to undergo a security screening that includes a
                  criminal history background check. Commercial drivers in
                  Maryland can apply for their Hazardous Material Endorsement
                  and receive the required security screening at the same time.
                </p>
                <p className="text-sm font-medium text-[#4AA6CF]" data-id="element-446">
                  Detailed instructions available on the Motor Vehicle
                  Administration website.
                </p>
              </motion.div>

              {/* Card D: Adoptions */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeInUp} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 hover:shadow-md transition-shadow" data-id="element-447">
                <div className="flex items-center gap-4 mb-6" data-id="element-448">
                  <div className="p-3 bg-[#F0F4FF] rounded-xl" data-id="element-449">
                    <HeartIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-450" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E1F55]" data-id="element-451">
                    Adoptions
                  </h3>
                </div>
                <div className="space-y-4 text-sm text-gray-600 leading-relaxed" data-id="element-452">
                  <p data-id="element-453">
                    <strong className="text-[#1E1F55]" data-id="element-454">
                      In-State Finalization:
                    </strong>
                    <br data-id="element-455" />
                    If you currently live in Maryland and the adoption will be
                    finalized in Maryland, follow the instructions of the agency
                    conducting your home study for a full background childcare
                    record check.
                  </p>
                  <p data-id="element-456">
                    <strong className="text-[#1E1F55]" data-id="element-457">
                      International Adoption:
                    </strong>
                    <br data-id="element-458" />
                    If the child is being adopted from outside the United
                    States, you may need a Gold Seal Letter in addition to the
                    childcare record check. Check with your adoption agency for
                    additional details.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CJIS Customer Service Section */}
      <section className="py-24 bg-[#0A0F1E] text-white" data-id="element-459">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-460">
          <div className="text-center mb-16" data-id="element-461">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-462">
              CJIS Customer Service
            </h2>
            <p className="text-[#4AA6CF] font-medium text-lg mb-6" data-id="element-463">
              Criminal Justice Information System - Central Repository
            </p>
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium" data-id="element-464">
              <AlertCircleIcon className="h-4 w-4" data-id="element-465" />
              CJIS Customer Service cannot disclose any criminal history.
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-466">
            {/* Left Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-467">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" data-id="element-468">
                <span className="w-8 h-1 bg-[#4AA6CF] rounded-full" data-id="element-469"></span>
                What CJIS Can Help With
              </h3>
              <ul className="space-y-6" data-id="element-470">
                {['Check the status of a current background history request (requires reference number or SSN).', 'Take orders for applicant fingerprint cards. Download the form, fax to 410-653-6320, or mail to CJIS-Central Repository.', 'Answer questions regarding the processing of criminal history.', 'Give instructions on how to obtain criminal history.', 'Help to determine your appropriate criminal history request type.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-471">
                    <CheckCircleIcon className="h-6 w-6 text-[#4AA6CF] flex-shrink-0" data-id="element-472" />
                    <span className="text-gray-300 leading-relaxed" data-id="element-473">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>

            {/* Right Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="space-y-6" data-id="element-474">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" data-id="element-475">
                <span className="w-8 h-1 bg-[#4AA6CF] rounded-full" data-id="element-476"></span>
                Contact Information
              </h3>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm flex items-start gap-4" data-id="element-477">
                <div className="bg-[#4AA6CF]/20 p-3 rounded-xl" data-id="element-478">
                  <ClockIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-479" />
                </div>
                <div data-id="element-480">
                  <h4 className="font-bold text-lg mb-1" data-id="element-481">Hours of Operation</h4>
                  <p className="text-gray-400" data-id="element-482">
                    Monday through Friday
                    <br data-id="element-483" />
                    8:00 a.m. - 5:00 p.m.
                  </p>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm flex items-start gap-4" data-id="element-484">
                <div className="bg-[#4AA6CF]/20 p-3 rounded-xl" data-id="element-485">
                  <PhoneIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-486" />
                </div>
                <div data-id="element-487">
                  <h4 className="font-bold text-lg mb-1" data-id="element-488">Phone Numbers</h4>
                  <p className="text-gray-400" data-id="element-489">
                    Baltimore City:{' '}
                    <a href="tel:4107644501" className="text-white hover:text-[#4AA6CF] transition-colors" data-id="element-490">
                      410-764-4501
                    </a>
                    <br data-id="element-491" />
                    Toll Free:{' '}
                    <a href="tel:18887950011" className="text-white hover:text-[#4AA6CF] transition-colors" data-id="element-492">
                      1-888-795-0011
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm flex items-start gap-4" data-id="element-493">
                <div className="bg-[#4AA6CF]/20 p-3 rounded-xl" data-id="element-494">
                  <PrinterIcon className="h-6 w-6 text-[#4AA6CF]" data-id="element-495" />
                </div>
                <div data-id="element-496">
                  <h4 className="font-bold text-lg mb-1" data-id="element-497">Fax</h4>
                  <p className="text-gray-400" data-id="element-498">410-653-6320</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fee Notice Section */}
      <section className="py-24 bg-white border-b border-gray-100" data-id="element-499">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-500">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-[#F0F4FF] rounded-3xl border border-[#E7ECF8] p-8 md:p-12 text-center" data-id="element-501">
            <CreditCardIcon className="h-12 w-12 text-[#4AA6CF] mx-auto mb-6" data-id="element-502" />
            <h2 className="text-3xl font-bold text-[#1E1F55] mb-4" data-id="element-503">
              Associated CJIS Fees
            </h2>
            <div className="w-16 h-1 bg-[#4AA6CF] rounded-full mx-auto mb-8" data-id="element-504"></div>

            <h3 className="text-xl font-bold text-[#1E1F55] mb-4" data-id="element-505">
              Federal Register - Notice of Fee Increase
            </h3>
            <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-506">
              Effective January 1, 2019 rates for submitting federal background
              checks via the Maryland Criminal Justice Information System -
              Central Repository increased in accordance with the fee schedule
              established by the Federal Bureau of Investigation. Cost to
              providers increased by $1.25 per fingerprint-based submission.
              Fingerprint-based volunteer submission increased by $0.50 cents.
            </p>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 max-w-xl mx-auto" data-id="element-507">
              <h4 className="font-bold text-[#1E1F55] mb-3" data-id="element-508">
                Accepted Payment Methods
              </h4>
              <p className="text-gray-600 mb-4" data-id="element-509">
                Fees are required to process each criminal background record
                check request. All fees must be paid by:
              </p>
              <div className="flex justify-center gap-4 mb-4" data-id="element-510">
                <span className="px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700 border border-gray-200" data-id="element-511">
                  Credit Card (Visa, MC, Discover)
                </span>
                <span className="px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700 border border-gray-200" data-id="element-512">
                  Check
                </span>
              </div>
              <p className="text-sm text-red-500 font-medium" data-id="element-513">
                The Central Repository cannot accept cash or money orders.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Existing Components */}
      <HowItWorks data-id="element-514" />
      <FAQ data-id="element-515" />
    </>;
}